import { Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { MatDatepicker } from "@angular/material/datepicker";
import { ActivatedRoute, Params, Router } from "@angular/router";
import { FormControl } from "@angular/forms";
import { DatePipe } from "@angular/common";
import { MatDialog } from "@angular/material/dialog";
import { PlanUpgradeDialogComponent } from "src/app/components/plan-upgrade-dialog/plan-upgrade-dialog.component";
import { Subscription, combineLatest, forkJoin, of } from "rxjs";
import moment from "moment";
import { HttpErrorResponse, HttpResponse } from "@angular/common/http";
import { debounceTime, catchError, map } from "rxjs/operators";
import { DateTime } from "luxon";
import { ProjectService } from "src/app/Services/project.service";
import { ScreenshotTimeEnrichment, ScreenshotTimeService, ScreenshotTimeView } from "src/app/Services/screenshot-time.service";
import { SharedService } from "src/app/Services/sharedService";
import { TokenEncryptionService } from "src/app/Services/token-encryption.service";
import { ShiftService } from "src/app/Services/shift.service";
import { Shift } from "src/app/models/shift.model";
import { ScreenshotFancyboxService } from "src/app/Services/screenshot-fancybox.service";
import { EntitlementsService } from "src/app/Services/entitlements.service";
import { MessagingServiceService } from "src/app/Services/messaging-service.service";
import { isRustAgentUser } from "src/app/agent-deploy/agent-deploy.config";
import { sysConfig } from "src/app/sysConfig";

type BucketSizeMinutes = 15 | 30 | 60 | 120 | 240;

const DAYPART_LABELS = ["Late Night", "Morning", "Afternoon", "Evening", "Night"] as const;
type DaypartLabel = (typeof DAYPART_LABELS)[number];

type MatchConfidence = "exact_match" | "near_match" | "no_match";

type ScreenshotItem = any & {
  _parsedUtcMs?: number | null;
  _bucketKey?: string;
  _windowName?: string | null;
  _matchConfidence?: MatchConfidence;
  _imgError?: boolean;
  _appIconErr?: boolean;
  timeDisplay?: any;
  timeagoInput?: Date | null;
  showAbsoluteTime?: boolean;
};

/** WP screenshots API context for Work Replay entitlement checks (SUSEE-1344). */
const WORK_REPLAY_SCREENSHOT_CONTEXT = "work_replay";

type TimelineBucket = {
  key: string;
  startUtcMs: number;
  endUtcMs: number;
  label: string;
  screenshots: ScreenshotItem[];
  usageSeconds: number;
  idleSeconds: number;
  state: "active" | "idle" | "mixed" | "active_no_screenshot" | "no_data";
};

@Component({
  selector: "app-work-replay",
  templateUrl: "./work-replay.component.html",
  styleUrls: ["./work-replay.component.css"],
  providers: [DatePipe],
  standalone: false,
})
export class WorkReplayComponent implements OnInit, OnDestroy {
  @ViewChild("picker") private datePicker?: MatDatepicker<Date>;

  bucketSize: BucketSizeMinutes = 60;
  filtersCollapsed = false;

  appFilterCtrl = new FormControl<string>("all");
  typeFilterCtrl = new FormControl<string>("all");
  /** View filter: selected day-part sections; empty = all times. */
  daypartFilterCtrl = new FormControl<string[]>([]);
  readonly daypartFilterOptions = DAYPART_LABELS;
  /** Full-day timeline (00:00–23:59 in company shift TZ) includes empty buckets by default. */
  showEmptySlotsCtrl = new FormControl<boolean>(true);
  employees: any[] = [];

  selectedDate: Date | null = null;
  minDate: any;
  maxDate: any;
  serverDate: any = new Date();

  userId: string | null = null;
  userDetails: any = null;

  loadingEmployees = false;
  /** Shown when the employee API fails or returns an unexpected shape. */
  employeesLoadError: string | null = null;
  loadingUser = false;
  loadingScreenshots = false;
  loadingLogs = false;

  shiftTimezone: string | null = null;
  shiftInfo: Shift | null = null;
  regionHint: string | null = null;
  /** True after company shift config is resolved (success or failure). Gates data fetch. */
  private shiftContextLoaded = false;
  /** Cached active company shifts; used to resolve per-employee assignment without extra API calls. */
  private companyShifts: Shift[] = [];
  /** Guards async employee-detail callbacks from applying a stale employee's shift. */
  private employeeDetailsRequestId = 0;
  private subs = new Subscription();

  screenshots: ScreenshotItem[] = [];
  filteredScreenshots: ScreenshotItem[] = [];
  screenshotsTotal = 0;
  screenshotsLoaded = 0;
  availableApps: string[] = [];

  logsByDate: any = null;
  flattenedLogSessions: Array<{
    inUtcMs: number;
    outUtcMs: number;
    appName?: string;
    windowName?: string;
    usageSeconds?: number;
    idleSeconds?: number;
  }> = [];

  buckets: TimelineBucket[] = [];
  bucketGroups: Array<{ label: string; buckets: TimelineBucket[]; bucketCount: number; screenshotCount: number }> = [];
  expandedBucketKeys = new Set<string>();
  collapsedDayparts = new Set<string>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private datePipe: DatePipe,
    private projectService: ProjectService,
    private screenshotTime: ScreenshotTimeService,
    private sharedService: SharedService,
    private tokenEncryptionService: TokenEncryptionService,
    private shiftService: ShiftService,
    private screenshotFancybox: ScreenshotFancyboxService,
    private entitlementsService: EntitlementsService,
    private dialog: MatDialog,
    private messagingService: MessagingServiceService,
  ) { }

  ngOnInit(): void {
    this.entitlementsService.syncFromSession();
    this.initServerDateRange();
    this.initShiftContext();
    // Snapshot first so child employee-search gets userId on the initial render (refresh / deep link).
    this.applyRouteState(this.route.snapshot.params, this.route.snapshot.queryParams);
    this.initRouteState();
    this.initReplayFilters();
    this.loadEmployees();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  get isLanding(): boolean {
    return !this.userId;
  }

  /** App filter closed label + glyph source (drives `appIconGlyph` pipe). */
  get appFilterTriggerLabel(): string {
    const raw = this.appFilterCtrl.value;
    const v = raw == null || raw === "" ? "all" : String(raw).toLowerCase();
    if (v === "all") {
      return "All apps";
    }
    const found = this.availableApps.find((a) => (a || "").toLowerCase() === v);
    return found || String(raw);
  }

  /** Selected day-parts; empty or all five = no narrowing. */
  get selectedDayparts(): string[] {
    return (this.daypartFilterCtrl.value || []).filter(Boolean);
  }

  get isDaypartFilterShowingAll(): boolean {
    const n = this.selectedDayparts.length;
    return n === 0 || n >= DAYPART_LABELS.length;
  }

  /** Time-of-day filter closed label. */
  get daypartFilterTriggerLabel(): string {
    const selected = this.selectedDayparts;
    if (this.isDaypartFilterShowingAll) {
      return "All times";
    }
    if (selected.length === 1) {
      return selected[0];
    }
    if (selected.length === 2) {
      return `${selected[0]}, ${selected[1]}`;
    }
    return `${selected[0]} + ${selected.length - 1} more`;
  }

  /** Icon for closed multi-select trigger. */
  get daypartFilterTriggerIcon(): string {
    const selected = this.selectedDayparts;
    if (this.isDaypartFilterShowingAll) {
      return "all";
    }
    if (selected.length === 1) {
      return selected[0];
    }
    return "all";
  }

  /** Bucket groups shown in the timeline (respects time-of-day filter). */
  get visibleBucketGroups(): Array<{
    label: string;
    buckets: TimelineBucket[];
    bucketCount: number;
    screenshotCount: number;
  }> {
    if (this.isDaypartFilterShowingAll) {
      return this.bucketGroups;
    }
    const allowed = new Set(this.selectedDayparts);
    return this.bucketGroups.filter((g) => allowed.has(g.label));
  }

  /** Type filter closed label (matches mat-option copy). */
  get typeFilterTriggerLabel(): string {
    const v = (this.typeFilterCtrl.value || "all").toLowerCase();
    if (v === "all") return "All types";
    return this.mapTypeLabel(v);
  }

  /**
   * Material Symbols name for Type filter (same glyphs as `app-ss-card` type chip).
   */
  matIconForTypeFilter(value: string | null | undefined): string {
    const v = String(value ?? "all").toLowerCase();
    if (v === "all") return "category";
    if (v === "periodic") return "schedule";
    if (v === "app-changed") return "swap_horiz";
    if (v === "admin-request") return "photo_camera";
    return "category";
  }

  /** Closed bucket-size label (matches mat-option copy). */
  get bucketSizeTriggerLabel(): string {
    return this.formatBucketSizeLabel(this.bucketSize);
  }

  formatBucketSizeLabel(m: number | BucketSizeMinutes): string {
    switch (Number(m) as BucketSizeMinutes) {
      case 15:
        return "15 min";
      case 30:
        return "30 min";
      case 60:
        return "1 hour";
      case 120:
        return "2 hours";
      case 240:
        return "4 hours";
      default:
        return "1 hour";
    }
  }

  /**
   * Material Symbols for bucket size: each step is a visibly distinct glyph
   * (fine slices → stopwatch → wall clock → single-day sheet → month grid).
   */
  matIconForBucketSize(m: number | null | undefined): string {
    switch (Number(m)) {
      case 15:
        return "more_time";
      case 30:
        return "timer";
      case 60:
        return "schedule";
      case 120:
        return "calendar_today";
      case 240:
        return "calendar_month";
      default:
        return "schedule";
    }
  }

  private initServerDateRange(): void {
    this.subs.add(
      this.projectService.getUser().subscribe((data: any) => {
        if (data?.meta?.today_date) {
          const parsedDate = new Date(data.meta.today_date);
          this.serverDate = isNaN(parsedDate.getTime()) ? new Date() : parsedDate;
        } else {
          this.serverDate = new Date();
        }

        const companyId =
          this.tokenEncryptionService.getEncryptedItem<any>("logedInUser")?.company_id ??
          data?.company_id;

        if (this.isWorkReplayPreviewOnly()) {
          this.subs.add(
            this.sharedService
              .getValidationDateRangeByCompany(this.serverDate, companyId, { maxDayOffset: -1 })
              .subscribe((yesterdayRange) => {
                const yesterday = this.toDateOnly(yesterdayRange.maxDate);
                this.minDate = yesterday;
                this.maxDate = yesterday;
                this.selectedDate = this.clampToAllowedWorkReplayDate(
                  this.selectedDate ?? yesterday
                );
                if (this.shiftContextLoaded && this.userId) {
                  this.loadWorkReplayData();
                }
              })
          );
          return;
        }

        this.subs.add(
          this.sharedService
            .getValidationDateRangeByCompany(this.serverDate, companyId, {
              lookbackDays: this.entitlementsService.getScreenshotsRetentionDays(),
            })
            .subscribe((range) => {
              this.minDate = range.minDate;
              this.maxDate = range.maxDate;

              if (this.selectedDate != null) {
                this.selectedDate = this.clampToAllowedWorkReplayDate(this.selectedDate);
                return;
              }

              // Default: yesterday in company shift timezone (same as dashboard date semantics).
              this.subs.add(
                this.sharedService
                  .getValidationDateRangeByCompany(this.serverDate, companyId, { maxDayOffset: -1 })
                  .subscribe((yesterdayRange) => {
                    this.selectedDate = this.toDateOnly(yesterdayRange.maxDate);
                    if (this.shiftContextLoaded && this.userId) {
                      this.loadWorkReplayData();
                    }
                  })
              );
            })
        );
      })
    );
  }

  private toDateOnly(value: Date): Date {
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }

  isWorkReplayPreviewOnly(): boolean {
    return this.entitlementsService.isWorkReplayPreviewOnly();
  }

  /** Basic preview: date navigation uses upgrade modal (same pattern as Live Monitoring). */
  isWorkReplayDateNavigationLocked(): boolean {
    return this.isWorkReplayPreviewOnly();
  }

  onDatepickerOpened(): void {
    if (!this.isWorkReplayDateNavigationLocked()) {
      return;
    }
    queueMicrotask(() => this.datePicker?.close());
    this.promptWorkReplayDateUpgrade();
  }

  private promptWorkReplayDateUpgrade(): void {
    this.promptWorkReplayUpgradeDialog({
      title: "Upgrade for full history",
      message:
        "Date navigation is included with full Work Replay on Professional and above. Basic preview is limited to yesterday.",
    });
  }

  private promptWorkReplayUpgradeDialog(options: {
    title: string;
    message: string;
    confirmLabel?: string;
    navigateOnConfirm?: boolean;
  }): void {
    const ref = this.dialog.open(PlanUpgradeDialogComponent, {
      width: "420px",
      maxWidth: "94vw",
      data: {
        title: options.title,
        message: options.message,
        confirmLabel: options.confirmLabel,
      },
    });
    ref.afterClosed().subscribe((confirmed) => {
      if (confirmed && options.navigateOnConfirm !== false) {
        void this.router.navigate(["/billings"], { queryParams: { intent: "upgrade" } });
      }
    });
  }

  private getYesterdayDate(serverDate: Date): Date {
    const d = new Date(serverDate);
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() - 1);
    return d;
  }

  private clampToAllowedWorkReplayDate(date: Date): Date {
    if (!this.isWorkReplayPreviewOnly()) {
      return date;
    }
    const allowed =
      this.minDate != null
        ? this.toDateOnly(new Date(this.minDate))
        : this.getYesterdayDate(this.serverDate);
    return new Date(allowed.getTime());
  }

  private initShiftContext(): void {
    const companyId = this.tokenEncryptionService.getEncryptedItem<any>("logedInUser")?.company_id;
    if (!companyId) {
      this.companyShifts = [];
      this.applyResolvedShift();
      this.markShiftContextReady();
      return;
    }

    this.subs.add(
      this.shiftService.getShifts(Number(companyId)).subscribe({
        next: (shifts) => {
          this.subs.add(
            this.hydrateShiftAssignments(Array.isArray(shifts) ? shifts : []).subscribe({
              next: (hydrated) => {
                this.companyShifts = hydrated;
                this.applyResolvedShift();
                this.markShiftContextReady();
              },
              error: () => {
                this.companyShifts = Array.isArray(shifts) ? shifts : [];
                this.applyResolvedShift();
                this.markShiftContextReady();
              },
            })
          );
        },
        error: () => {
          this.companyShifts = [];
          this.shiftInfo = null;
          this.shiftTimezone = null;
          this.regionHint = null;
          this.markShiftContextReady();
        },
      })
    );
  }

  /**
   * Populate employee_ids from GET /shifts/{id}/employees when the list payload omits them.
   * Shift Management uses this endpoint as the assignment source of truth.
   */
  private hydrateShiftAssignments(shifts: Shift[]) {
    const needsHydration = shifts.filter(
      (s) => Number(s?.id) > 0 && (!Array.isArray(s.employee_ids) || s.employee_ids.length === 0)
    );
    if (!needsHydration.length) {
      return of(shifts);
    }

    return forkJoin(
      needsHydration.map((s) =>
        this.shiftService.getShiftEmployees(Number(s.id)).pipe(
          map((employees) => ({
            shiftId: Number(s.id),
            ids: (employees || [])
              .filter((e) => e?.on_this_shift)
              .map((e) => Number(e.user_id))
              .filter((id) => Number.isFinite(id)),
          })),
          catchError(() => of({ shiftId: Number(s.id), ids: [] as number[] }))
        )
      )
    ).pipe(
      map((rows) => {
        const byId = new Map(rows.map((r) => [r.shiftId, r.ids]));
        return shifts.map((s) => {
          const ids = byId.get(Number(s.id));
          if (!ids) {
            return s;
          }
          return {
            ...s,
            employee_ids: ids,
            employee_count: ids.length || s.employee_count || 0,
          };
        });
      })
    );
  }

  /**
   * Resolve header from Shift Management assignment only (employee_ids).
   * Do not use user-meta shift ids — they often hold the company default and overwrite the real assignment.
   * Unassigned employees keep shiftInfo null (UI shows "Shift not assigned").
   * Day-bound timezone still falls back to company first-shift TZ so timeline math stays stable.
   */
  private applyResolvedShift(): void {
    const resolved = this.userId
      ? this.shiftService.resolveEmployeeShift(this.companyShifts, this.userId)
      : null;
    this.shiftInfo = resolved;
    const tzSource = resolved?.timezone ?? this.companyShifts[0]?.timezone ?? null;
    this.shiftTimezone = tzSource && this.screenshotTime.isValidIanaZone(tzSource) ? tzSource : null;
    this.regionHint = this.getWorkReplayRegionHint();
  }

  /** True after company shifts load attempt finished (success or failure). */
  get shiftContextReady(): boolean {
    return this.shiftContextLoaded;
  }

  /** Work Replay shows office (shift) time first; hint copy matches that behavior. */
  private getWorkReplayRegionHint(): string | null {
    if (!this.shiftTimezone || !this.screenshotTime.isValidIanaZone(this.shiftTimezone)) {
      return null;
    }
    const viewerZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (viewerZone === this.shiftTimezone) {
      return null;
    }
    try {
      const now = DateTime.now();
      const za = now.setZone(viewerZone);
      const zb = now.setZone(this.shiftTimezone);
      if (za.isValid && zb.isValid && za.offset === zb.offset) {
        return null;
      }
    } catch {
      /* fall through */
    }
    const short = this.shiftTimezone.split("/").pop()?.replace(/_/g, " ") ?? this.shiftTimezone;
    return `Full day (12:00 AM–11:59 PM) uses your company timezone (${short}). Times are shown in that zone, not UTC.`;
  }

  private markShiftContextReady(): void {
    if (this.shiftContextLoaded) {
      return;
    }
    this.shiftContextLoaded = true;
    if (this.userId && this.selectedDate) {
      this.loadWorkReplayData();
    }
  }

  private initRouteState(): void {
    this.subs.add(
      combineLatest([this.route.params, this.route.queryParams]).subscribe(
        ([params, queryParams]: [Params, Params]) => {
          this.applyRouteState(params, queryParams);
        }
      )
    );
  }

  private applyRouteState(params: Params, queryParams: Params): void {
    const nextUserId = queryParams?.employeeId ? String(queryParams.employeeId) : null;
    const userChanged = this.userId !== nextUserId;
    this.userId = nextUserId;

    const qpDate = typeof queryParams?.date === "string" ? queryParams.date : null;
    let dateChanged = false;
    if (qpDate) {
      const parsed = new Date(qpDate);
      if (!isNaN(parsed.getTime())) {
        const clamped = this.clampToAllowedWorkReplayDate(parsed);
        dateChanged = this.selectedDate?.getTime() !== clamped.getTime();
        this.selectedDate = clamped;
      }
    }

    let filterChanged = false;
    let requiresRebuild = false;

    if (queryParams['bucketSize']) {
      const bs = Number(queryParams['bucketSize']) as BucketSizeMinutes;
      if (this.bucketSize !== bs) {
        this.bucketSize = bs;
        filterChanged = true;
        requiresRebuild = true;
      }
    }

    const appVal = queryParams['app'] || 'all';
    if (this.appFilterCtrl.value !== appVal) {
      this.appFilterCtrl.setValue(appVal, { emitEvent: false });
      filterChanged = true;
    }

    const typeVal = queryParams['type'] || 'all';
    if (this.typeFilterCtrl.value !== typeVal) {
      this.typeFilterCtrl.setValue(typeVal, { emitEvent: false });
      filterChanged = true;
    }

    const daypartsStr = queryParams['dayparts'];
    const newDayparts = daypartsStr ? daypartsStr.split(',') : [];
    if (this.daypartFilterCtrl.value?.join(',') !== newDayparts.join(',')) {
      this.daypartFilterCtrl.setValue(newDayparts, { emitEvent: false });
      filterChanged = true;
    }

    const showEmptyStr = queryParams['showEmpty'];
    const showEmpty = showEmptyStr ? showEmptyStr === 'true' : true;
    if (this.showEmptySlotsCtrl.value !== showEmpty) {
      this.showEmptySlotsCtrl.setValue(showEmpty, { emitEvent: false });
      filterChanged = true;
      requiresRebuild = true;
    }

    if (this.userId) {
      if (userChanged) {
        // Clear previous employee details so preferred-shift meta cannot leak across users.
        this.userDetails = null;
        this.applyResolvedShift();
        this.loadEmployeeDetails(this.userId);
      }
      if ((userChanged || dateChanged) && this.shiftContextLoaded) {
        this.loadWorkReplayData();
      } else if (filterChanged && !userChanged && !dateChanged) {
        this.applyReplayFilters();
        if (requiresRebuild) {
          this.rebuildBuckets();
        }
      }
    } else {
      this.employeeDetailsRequestId++;
      this.userDetails = null;
      this.applyResolvedShift();
      this.screenshots = [];
      this.filteredScreenshots = [];
      this.buckets = [];
      this.flattenedLogSessions = [];
    }
  }

  private updateQueryParams(): void {
    const queryParams: Params = {};

    if (this.bucketSize !== 60) {
      queryParams['bucketSize'] = this.bucketSize;
    } else {
      queryParams['bucketSize'] = null;
    }

    if (this.appFilterCtrl.value && this.appFilterCtrl.value !== 'all') {
      queryParams['app'] = this.appFilterCtrl.value;
    } else {
      queryParams['app'] = null;
    }

    if (this.typeFilterCtrl.value && this.typeFilterCtrl.value !== 'all') {
      queryParams['type'] = this.typeFilterCtrl.value;
    } else {
      queryParams['type'] = null;
    }

    const dayparts = this.daypartFilterCtrl.value;
    if (dayparts && dayparts.length > 0) {
      queryParams['dayparts'] = dayparts.join(',');
    } else {
      queryParams['dayparts'] = null;
    }

    if (this.showEmptySlotsCtrl.value !== true) {
      queryParams['showEmpty'] = this.showEmptySlotsCtrl.value ? 'true' : 'false';
    } else {
      queryParams['showEmpty'] = null;
    }

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: "merge",
      replaceUrl: true,
    });
  }

  private initReplayFilters(): void {
    this.subs.add(
      this.appFilterCtrl.valueChanges.pipe(debounceTime(150)).subscribe(() => {
        this.updateQueryParams();
        this.applyReplayFilters();
      })
    );
    this.subs.add(
      this.typeFilterCtrl.valueChanges.pipe(debounceTime(150)).subscribe(() => {
        this.updateQueryParams();
        this.applyReplayFilters();
      })
    );
    this.subs.add(
      this.daypartFilterCtrl.valueChanges.pipe(debounceTime(150)).subscribe(() => {
        this.updateQueryParams();
      })
    );
    this.subs.add(
      this.showEmptySlotsCtrl.valueChanges.pipe(debounceTime(50)).subscribe(() => {
        this.updateQueryParams();
        this.rebuildBuckets();
      })
    );
  }

  private loadEmployees(): void {
    this.loadingEmployees = true;
    this.employeesLoadError = null;
    // Force refresh so we never reuse a failed/stale shareReplay cache from ProjectService.
    this.subs.add(
      this.projectService.getUserList(0, 0, true).subscribe({
        next: (res: any) => {
          const list = this.normalizeEmployeesFromApi(res);
          this.employees = list;
          this.loadingEmployees = false;
          const unrecognizedShape =
            res != null &&
            typeof res === "object" &&
            !Array.isArray(res) &&
            !Array.isArray(res?.data) &&
            !Array.isArray(res?.users);
          if (list.length === 0 && unrecognizedShape) {
            this.employeesLoadError =
              "Employee list was empty or in an unexpected format. Try Retry, or refresh the page.";
          }
        },
        error: () => {
          this.employees = [];
          this.loadingEmployees = false;
          this.employeesLoadError =
            "Could not load employees. Check your connection and try again.";
        },
      })
    );
  }

  /** Normalize getUserList payloads (array, or { data } / { users }) like other admin screens. */
  private normalizeEmployeesFromApi(res: any): any[] {
    if (Array.isArray(res)) {
      return res.filter((u) => u != null);
    }
    if (res && Array.isArray(res.data)) {
      return res.data.filter((u) => u != null);
    }
    if (res && Array.isArray(res.users)) {
      return res.users.filter((u) => u != null);
    }
    return [];
  }

  retryLoadEmployees(): void {
    this.loadEmployees();
  }

  onEmployeeSelected(userId: string | number | null): void {
    const empty = userId === "" || userId === null || userId === undefined;
    const uid = empty ? null : String(userId);

    if (this.userId === uid) {
      return;
    }

    const dateStr = this.selectedDate
      ? this.datePipe.transform(this.selectedDate, "yyyy-MM-dd")
      : null;

    const queryParams: Params = {
      employeeId: uid,
      ...(dateStr ? { date: dateStr } : {})
    };

    this.router.navigate(["/work-replay"], {
      queryParams,
      queryParamsHandling: "merge",
    });
  }

  onDateChanged(date: Date | null): void {
    if (!date) {
      return;
    }
    if (this.isWorkReplayDateNavigationLocked()) {
      this.promptWorkReplayDateUpgrade();
      return;
    }
    this.selectedDate = this.clampToAllowedWorkReplayDate(date);
    if (!this.userId) return;

    const dateStr = this.selectedDate
      ? this.datePipe.transform(this.selectedDate, "yyyy-MM-dd")
      : null;

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: dateStr ? { date: dateStr } : { date: null },
      queryParamsHandling: "merge",
      replaceUrl: true,
    });

    if (this.shiftContextLoaded) {
      this.loadWorkReplayData();
    }
  }

  setBucketSize(m: BucketSizeMinutes): void {
    this.bucketSize = m;
    this.updateQueryParams();
    this.rebuildBuckets();
  }

  toggleBucket(key: string): void {
    if (this.expandedBucketKeys.has(key)) {
      this.expandedBucketKeys.delete(key);
      return;
    }
    // Enforce max 5 expanded buckets to balance readability with performance.
    if (this.expandedBucketKeys.size >= 5) {
      const oldest = Array.from(this.expandedBucketKeys.values())[0];
      if (oldest) {
        this.expandedBucketKeys.delete(oldest);
      }
    }
    this.expandedBucketKeys.add(key);
  }

  isBucketExpanded(key: string): boolean {
    return this.expandedBucketKeys.has(key);
  }

  trackBucket = (_: number, b: TimelineBucket) => b.key;
  trackScreenshot = (_: number, ss: ScreenshotItem) => ss?.id ?? ss?.image_url ?? _;

  clearReplayFilters(): void {
    this.appFilterCtrl.setValue("all", { emitEvent: false });
    this.typeFilterCtrl.setValue("all", { emitEvent: false });
    this.daypartFilterCtrl.setValue([], { emitEvent: false });
    this.updateQueryParams();
    this.applyReplayFilters();
  }

  toggleFiltersCollapsed(): void {
    this.filtersCollapsed = !this.filtersCollapsed;
  }

  mapTypeLabel(type: string | undefined | null): string {
    const val = (type || "").trim().toLowerCase();
    if (val === "periodic") return "Periodic";
    if (val === "app-changed") return "App Switch";
    if (val === "admin-request") return "On Demand";
    if (!val) return "Unknown";
    return val
      .split(/[-_\s]+/)
      .filter(Boolean)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");
  }

  /** Same chip modifier classes as Screenshots / `app-ss-card`. */
  getTriggerChipClass(type: string | undefined | null): string {
    switch ((type || "").trim().toLowerCase()) {
      case "app-changed":
        return "screenshot-type-chip--appswitch";
      case "admin-request":
        return "screenshot-type-chip--ondemand";
      default:
        return "screenshot-type-chip--periodic";
    }
  }

  onAppIconError(ss: ScreenshotItem | null | undefined): void {
    if (ss) {
      ss._appIconErr = true;
    }
  }

  getScreenshotImageSrc(ss: ScreenshotItem): string {
    return ss?.image_url || ss?.large || ss?.thumbnail || "";
  }

  onScreenshotImageError(ss: ScreenshotItem): void {
    ss._imgError = true;
  }

  getSummaryCountByType(type: string): number {
    return this.filteredScreenshots.filter((s) => (s?.type || "").toLowerCase() === type.toLowerCase()).length;
  }

  get topCapturedApp(): string {
    if (!this.filteredScreenshots.length) return "—";
    const counts = new Map<string, number>();
    for (const s of this.filteredScreenshots) {
      const app = (s?.app_name || "Unknown app").trim() || "Unknown app";
      counts.set(app, (counts.get(app) || 0) + 1);
    }
    let top = "—";
    let topCount = -1;
    counts.forEach((c, app) => {
      if (c > topCount) {
        topCount = c;
        top = app;
      }
    });
    return top;
  }

  get hasActiveFilters(): boolean {
    return (this.appFilterCtrl.value || "all") !== "all"
      || (this.typeFilterCtrl.value || "all") !== "all"
      || !this.isDaypartFilterShowingAll;
  }

  get noResultAfterFilter(): boolean {
    return !this.loadingScreenshots && this.screenshots.length > 0 && this.filteredScreenshots.length === 0 && this.hasActiveFilters;
  }

  get noResultAfterDaypartFilter(): boolean {
    return (
      !this.loadingScreenshots &&
      this.bucketGroups.length > 0 &&
      this.visibleBucketGroups.length === 0 &&
      !this.isDaypartFilterShowingAll
    );
  }

  get shiftHoursSummary(): string | null {
    const s = this.shiftInfo;
    const tz = this.shiftTimezone;
    if (!s || !s.start_time || !s.end_time) return null;
    const start = String(s.start_time).substring(0, 5);
    const end = String(s.end_time).substring(0, 5);
    if (!start || !end) return null;
    const overnight = s.is_overnight ? " (+1 day)" : "";
    const tzLabel = tz ? ` · ${tz}` : "";
    const name = s.shift_name ? `${s.shift_name} · ` : "";
    return `${name}${start}–${end}${overnight}${tzLabel}`;
  }

  getBucketTopApp(bucket: TimelineBucket): string {
    if (!bucket?.screenshots?.length) return "Unknown app";
    const counts = new Map<string, number>();
    for (const s of bucket.screenshots) {
      const app = (s?.app_name || "Unknown app").trim() || "Unknown app";
      counts.set(app, (counts.get(app) || 0) + 1);
    }
    let top = "Unknown app";
    let n = -1;
    counts.forEach((v, k) => {
      if (v > n) {
        n = v;
        top = k;
      }
    });
    return top;
  }

  getBucketTopAppDisplay(bucket: TimelineBucket): string {
    const top = this.getBucketTopApp(bucket);
    if (!top) return "—";
    const norm = top.toLowerCase().trim();
    if (norm === "unknown app" || norm === "unknown") return "—";
    return top;
  }

  getBucketTypeBreakdown(bucket: TimelineBucket): string {
    const counts = new Map<string, number>();
    for (const s of bucket?.screenshots || []) {
      const t = this.mapTypeLabel(s?.type);
      counts.set(t, (counts.get(t) || 0) + 1);
    }
    return Array.from(counts.entries())
      .map(([k, v]) => `${v} ${k}`)
      .join(", ");
  }

  getBucketStateLabel(bucket: TimelineBucket): string {
    switch (bucket.state) {
      case "active":
        return "Usage";
      case "idle":
        return "Idle";
      case "mixed":
        return "Usage + Idle";
      case "active_no_screenshot":
        return "Tracked (No Screenshot)";
      default:
        return "No Activity";
    }
  }

  getBucketStateClass(bucket: TimelineBucket): string {
    return `wr-state-chip--${bucket.state}`;
  }

  getBucketStateDotClass(bucket: TimelineBucket): string {
    return `wr-state-dot--${bucket.state}`;
  }

  toggleDaypart(label: string): void {
    if (this.collapsedDayparts.has(label)) {
      this.collapsedDayparts.delete(label);
      return;
    }
    this.collapsedDayparts.add(label);
  }

  isDaypartCollapsed(label: string): boolean {
    return this.collapsedDayparts.has(label);
  }

  // ── Day navigation ──────────────────────────────────────────────────────────

  get canGoPrevDay(): boolean {
    if (!this.selectedDate) return false;
    const d = new Date(this.selectedDate);
    d.setDate(d.getDate() - 1);
    if (!this.minDate) return true;
    return d.getTime() >= new Date(this.minDate).getTime();
  }

  get canGoNextDay(): boolean {
    if (!this.selectedDate) return false;
    const d = new Date(this.selectedDate);
    d.setDate(d.getDate() + 1);
    if (!this.maxDate) return true;
    return d.getTime() <= new Date(this.maxDate).getTime();
  }

  prevDay(): void {
    if (this.isWorkReplayDateNavigationLocked()) {
      this.promptWorkReplayDateUpgrade();
      return;
    }
    if (!this.selectedDate || !this.canGoPrevDay) return;
    const d = new Date(this.selectedDate);
    d.setDate(d.getDate() - 1);
    this.onDateChanged(d);
  }

  nextDay(): void {
    if (this.isWorkReplayDateNavigationLocked()) {
      this.promptWorkReplayDateUpgrade();
      return;
    }
    if (!this.selectedDate || !this.canGoNextDay) return;
    const d = new Date(this.selectedDate);
    d.setDate(d.getDate() + 1);
    this.onDateChanged(d);
  }

  // ── Day totals ───────────────────────────────────────────────────────────────

  get totalUsageFormatted(): string {
    const sec = this.flattenedLogSessions.reduce((s, l) => s + (l.usageSeconds || 0), 0);
    return this.formatDurSec(sec);
  }

  get totalIdleFormatted(): string {
    const sec = this.flattenedLogSessions.reduce((s, l) => s + (l.idleSeconds || 0), 0);
    return this.formatDurSec(sec);
  }

  private formatDurSec(sec: number): string {
    if (!sec || sec <= 0) return '—';
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    if (h > 0 && m > 0) return `${h}h ${m}m`;
    if (h > 0) return `${h}h`;
    return `${m}m`;
  }

  // ── Employee context ─────────────────────────────────────────────────────────

  get selectedEmployeeName(): string {
    if (!this.userId) return '';
    const emp = (this.employees || []).find((e: any) => String(e?.id) === String(this.userId));
    return emp?.name || (this.userDetails as any)?.name || '';
  }

  readonly sysConfig = sysConfig;

  get showRustAgentBetaBadge(): boolean {
    if (!sysConfig.rustAgentBetaBadgeEnabled || !this.userId) {
      return false;
    }
    const emp = (this.employees || []).find((e: any) => String(e?.id) === String(this.userId));
    if (isRustAgentUser(emp) || isRustAgentUser(this.userDetails)) {
      return true;
    }
    const fbUser = this.messagingService
      .getLatestFirebaseUsersSnapshot()
      .find((u) => String(u?.id) === String(this.userId));
    return isRustAgentUser(fbUser);
  }

  // ── Day-part icon ────────────────────────────────────────────────────────────

  getDaypartIcon(label: string): string {
    return this.matIconForDaypartFilter(label);
  }

  matIconForDaypartFilter(value: string | null | undefined): string {
    switch (value) {
      case "all":
        return "schedule";
      case "Late Night":
        return "bedtime";
      case "Morning":
        return "wb_sunny";
      case "Afternoon":
        return "partly_cloudy_day";
      case "Evening":
        return "wb_twilight";
      case "Night":
        return "nights_stay";
      default:
        return "schedule";
    }
  }

  /** Clock-hour segment in company shift TZ (full day runs 12:00 AM → 11:59 PM). */
  private dayPartLabelForHour(hour: number): string {
    if (hour >= 0 && hour < 5) {
      return "Late Night";
    }
    if (hour >= 5 && hour < 12) {
      return "Morning";
    }
    if (hour >= 12 && hour < 17) {
      return "Afternoon";
    }
    if (hour >= 17 && hour < 22) {
      return "Evening";
    }
    return "Night";
  }

  getBucketTimeSummary(bucket: TimelineBucket): string {
    const usage = Math.round((bucket.usageSeconds || 0) / 60);
    const idle = Math.round((bucket.idleSeconds || 0) / 60);
    const parts: string[] = [];
    if (usage > 0) parts.push(`${usage}m usage`);
    if (idle > 0) parts.push(`${idle}m idle`);
    return parts.join(' • ');
  }

  getBucketScreenshotCountText(bucket: TimelineBucket): string {
    const count = bucket?.screenshots?.length || 0;
    if (count <= 0) return "No screenshots";
    return `${count} screenshot${count === 1 ? "" : "s"}`;
  }

  getBucketSecondaryMeta(bucket: TimelineBucket): string {
    if (!bucket?.screenshots?.length) return "";
    const parts: string[] = [];
    const app = this.getBucketTopAppDisplay(bucket);
    if (app && app !== "—") parts.push(app);
    const typeBreakdown = this.getBucketTypeBreakdown(bucket);
    if (typeBreakdown) parts.push(typeBreakdown);
    return parts.join(" • ");
  }

  openScreenshotPreview(ss: ScreenshotItem, bucket: TimelineBucket): void {
    const list = Array.isArray(bucket?.screenshots) ? bucket.screenshots : [];
    if (!list.length) {
      return;
    }
    const startIndex = Math.max(
      0,
      list.findIndex((x) => (x?.id != null && ss?.id != null ? String(x.id) === String(ss.id) : x === ss))
    );
    const u = this.userDetails;
    this.screenshotFancybox.open({
      items: list,
      startIndex,
      showWindow: true,
      shiftTimezone: this.shiftTimezone,
      employee: u
        ? {
            name: u.name || u.full_name,
            avatar: u.profile_image_url || u.image_url || u.image || u.avatar,
          }
        : null,
    });
  }

  private loadEmployeeDetails(userId: string): void {
    this.loadingUser = true;
    const requestId = ++this.employeeDetailsRequestId;
    this.subs.add(
      this.projectService.getEmployeeData(userId).subscribe({
        next: (data: any) => {
          if (requestId !== this.employeeDetailsRequestId || this.userId !== String(userId)) {
            return;
          }
          this.userDetails = data?.user || data || null;
          this.loadingUser = false;
        },
        error: () => {
          if (requestId !== this.employeeDetailsRequestId || this.userId !== String(userId)) {
            return;
          }
          this.userDetails = null;
          this.loadingUser = false;
        },
      })
    );
  }

  private loadWorkReplayData(): void {
    if (!this.userId || !this.selectedDate) {
      return;
    }
    this.loadScreenshotsForSelectedDay();
    this.loadLogsForSelectedDay();
  }

  private loadScreenshotsForSelectedDay(): void {
    if (!this.userId) return;
    const bounds = this.getSelectedDayBoundsForApi();
    if (!bounds) return;

    this.loadingScreenshots = true;
    this.screenshots = [];
    this.filteredScreenshots = [];
    this.screenshotsTotal = 0;
    this.screenshotsLoaded = 0;
    this.availableApps = [];
    this.expandedBucketKeys.clear();

    const perPage = 100;
    const firstPage = 1;
    this.subs.add(
      this.projectService
        .getSingleSS(
          this.userId,
          perPage,
          firstPage,
          bounds.start,
          bounds.end,
          WORK_REPLAY_SCREENSHOT_CONTEXT
        )
        .subscribe({
        next: (res: HttpResponse<any>) => {
          const firstData = ((res?.body as any)?.data || []) as any[];
          const total = Number(res?.headers?.get("X-WP-Total") || firstData.length || 0);
          const totalPages = Math.max(1, Math.ceil(total / perPage));
          if (totalPages <= 1) {
            this.finalizeScreenshotsLoad(firstData, total);
            return;
          }

          const requests = [];
          for (let page = 2; page <= totalPages; page++) {
            requests.push(
              this.projectService
                .getSingleSS(
                  this.userId!,
                  perPage,
                  page,
                  bounds.start,
                  bounds.end,
                  WORK_REPLAY_SCREENSHOT_CONTEXT
                )
                .pipe(catchError(() => of(null)))
            );
          }

          this.subs.add(
            forkJoin(requests).subscribe((rest: any[]) => {
              const all = [...firstData];
              for (const r of rest) {
                const pageData = ((r?.body as any)?.data || []) as any[];
                all.push(...pageData);
              }
              this.finalizeScreenshotsLoad(all, total);
            })
          );
        },
        error: (err) => this.handleScreenshotsLoadError(err),
      })
    );
  }

  private handleScreenshotsLoadError(err: unknown): void {
    const httpErr = err as HttpErrorResponse;
    const body = httpErr?.error;
    const apiMessage =
      body && typeof body === "object" && typeof (body as { message?: string }).message === "string"
        ? (body as { message: string }).message
        : null;
    const apiCode =
      body && typeof body === "object" && (body as { code?: string }).code
        ? String((body as { code: string }).code)
        : "";

    const showUpgrade =
      this.isWorkReplayPreviewOnly() ||
      httpErr?.status === 403 ||
      /plan|upgrade|preview|entitlement|work_replay|yesterday/i.test(apiMessage || "") ||
      /plan|upgrade|preview|entitlement|work_replay|yesterday/i.test(apiCode);

    this.promptWorkReplayUpgradeDialog({
      title: showUpgrade ? "Upgrade for full history" : "Could not load screenshots",
      message:
        apiMessage ||
        "Could not load screenshots for this date. Try again or choose another day.",
      confirmLabel: showUpgrade ? "View plans" : "OK",
      navigateOnConfirm: showUpgrade,
    });

    this.screenshots = [];
    this.filteredScreenshots = [];
    this.screenshotsTotal = 0;
    this.screenshotsLoaded = 0;
    this.availableApps = [];
    this.buckets = [];
    this.bucketGroups = [];
    this.loadingScreenshots = false;
  }

  private decorateScreenshot(s: any): ScreenshotItem {
    const raw = s?.ist_time ?? s?.utc_time ?? s?.captured_at ?? s?.captured_time;
    const utc = this.screenshotTime.parseCaptureToUtc(raw, this.shiftTimezone);
    const ms = utc && utc.isValid ? utc.toMillis() : null;
    const base = this.screenshotTime.getEnrichedScreenshotTime(raw, this.shiftTimezone);
    const wrTime = this.enrichWorkReplayScreenshotTime(raw, base);
    return {
      ...s,
      captured_at: s?.ist_time || s?.captured_at,
      captured_time: s?.ist_time || s?.captured_time,
      _parsedUtcMs: ms,
      _windowName: null,
      _matchConfidence: "no_match",
      _imgError: false,
      ...(wrTime ?? {}),
    };
  }

  /**
   * Work Replay: primary line is shift (office) wall time; viewer time is secondary when it differs.
   */
  private enrichWorkReplayScreenshotTime(
    raw: string | number | Date | null | undefined,
    base: ScreenshotTimeEnrichment | null
  ): ScreenshotTimeEnrichment | null {
    if (!base) {
      return null;
    }
    if (!this.shiftTimezone || !this.screenshotTime.isValidIanaZone(this.shiftTimezone)) {
      return base;
    }
    const utc = this.screenshotTime.parseCaptureToUtc(raw, this.shiftTimezone);
    if (!utc?.isValid) {
      return base;
    }

    const officePrimary = this.formatInstantInZone(utc.toMillis(), this.shiftTimezone);
    const viewerZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const viewerWall = this.formatInstantInZone(utc.toMillis(), viewerZone);
    let secondaryLine: string | undefined;
    if (viewerWall !== officePrimary) {
      secondaryLine = `Your time · ${viewerWall}`;
    }

    const timeDisplay: ScreenshotTimeView = {
      primaryAbsolute: officePrimary,
      secondaryLine,
      tooltip: this.screenshotTime.buildCardHoverTooltip(raw, this.shiftTimezone),
    };

    return { ...base, timeDisplay };
  }

  private formatInstantInZone(utcMs: number, ianaZone: string): string {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
      timeZone: ianaZone,
    }).format(new Date(utcMs));
  }

  private loadLogsForSelectedDay(): void {
    if (!this.userId || !this.selectedDate) return;

    const ymd = this.datePipe.transform(this.selectedDate, "yyyy-MM-dd");
    if (!ymd) return;

    this.loadingLogs = true;
    this.flattenedLogSessions = [];

    this.subs.add(
      this.projectService.getUserApplicationLogs(this.userId, ymd, ymd).subscribe((res: any) => {
        this.logsByDate = res;
        this.flattenedLogSessions = this.flattenSessions(res);
        this.matchScreenshotsToLogs();
        this.applyReplayFilters();
        this.loadingLogs = false;
      }, () => {
        this.logsByDate = null;
        this.flattenedLogSessions = [];
        this.matchScreenshotsToLogs();
        this.applyReplayFilters();
        this.loadingLogs = false;
      })
    );
  }

  private flattenSessions(raw: any): Array<{ inUtcMs: number; outUtcMs: number; appName?: string; windowName?: string; usageSeconds?: number; idleSeconds?: number }> {
    const out: Array<{ inUtcMs: number; outUtcMs: number; appName?: string; windowName?: string; usageSeconds?: number; idleSeconds?: number }> = [];
    if (!raw || typeof raw !== "object") return out;

    for (const key of Object.keys(raw)) {
      const day = raw[key];
      const sessions = Array.isArray(day?.sessions) ? day.sessions : [];
      for (const s of sessions) {
        const inUtc = this.screenshotTime.parseCaptureToUtc(s?.in, this.shiftTimezone);
        const outUtc = this.screenshotTime.parseCaptureToUtc(s?.out, this.shiftTimezone);
        if (!inUtc?.isValid || !outUtc?.isValid) continue;
        out.push({
          inUtcMs: inUtc.toMillis(),
          outUtcMs: outUtc.toMillis(),
          appName: s?.appName,
          windowName: s?.windowName,
          usageSeconds: this.parseDurationToSeconds(s?.usage),
          idleSeconds: this.parseDurationToSeconds(s?.idle),
        });
      }
    }

    out.sort((a, b) => a.inUtcMs - b.inUtcMs);
    return out;
  }

  private matchScreenshotsToLogs(): void {
    if (!Array.isArray(this.screenshots) || this.screenshots.length === 0) return;
    const logs = this.flattenedLogSessions;
    if (!Array.isArray(logs) || logs.length === 0) {
      this.screenshots = this.screenshots.map(s => ({ ...s, _windowName: null, _matchConfidence: "no_match" }));
      return;
    }

    const toleranceMs = 3 * 60 * 1000;
    this.screenshots = this.screenshots.map((ss) => {
      const t = ss._parsedUtcMs;
      if (!t) {
        return { ...ss, _windowName: null, _matchConfidence: "no_match" };
      }

      // 1) Containing interval wins.
      const containing = logs.find((l) => t >= l.inUtcMs && t <= l.outUtcMs);
      if (containing) {
        return { ...ss, _windowName: containing.windowName || null, _matchConfidence: "exact_match" };
      }

      // 2) Nearest within tolerance.
      let best: any = null;
      let bestDelta = Number.POSITIVE_INFINITY;
      for (const l of logs) {
        const delta = Math.min(Math.abs(t - l.inUtcMs), Math.abs(t - l.outUtcMs));
        if (delta < bestDelta) {
          best = l;
          bestDelta = delta;
        }
      }
      if (best && bestDelta <= toleranceMs) {
        return { ...ss, _windowName: best.windowName || null, _matchConfidence: "near_match" };
      }

      return { ...ss, _windowName: null, _matchConfidence: "no_match" };
    });
  }

  private rebuildBuckets(): void {
    this.buckets = this.buildBucketsForSelectedDay(
      this.filteredScreenshots,
      this.bucketSize,
      !!this.showEmptySlotsCtrl.value
    );
    this.bucketGroups = this.groupBucketsByDayPart(this.buckets);
    this.seedExpandedBuckets();
  }

  private applyReplayFilters(): void {
    const appFilter = (this.appFilterCtrl.value || "all").toLowerCase();
    const typeFilter = (this.typeFilterCtrl.value || "all").toLowerCase();

    this.filteredScreenshots = (this.screenshots || []).filter((s) => {
      const app = (s?.app_name || "").toLowerCase();
      const type = (s?.type || "").toLowerCase();

      if (appFilter !== "all" && app !== appFilter) return false;
      if (typeFilter !== "all" && type !== typeFilter) return false;
      return true;
    });

    this.rebuildBuckets();
  }

  private refreshAvailableAppsFromLoadedScreenshots(): void {
    this.availableApps = Array.from(
      new Set(
        (this.screenshots || [])
          .map((s) => (s?.app_name || "").trim())
          .filter((v) => !!v)
      )
    ).sort((a, b) => a.localeCompare(b));
  }

  private finalizeScreenshotsLoad(rawRows: any[], total: number): void {
    const seen = new Set<string>();
    const deduped: any[] = [];
    for (const row of rawRows || []) {
      const id = String(row?.id ?? "");
      if (id && seen.has(id)) continue;
      if (id) seen.add(id);
      deduped.push(row);
    }
    this.screenshotsTotal = Math.max(total, deduped.length);
    this.screenshots = deduped.map((s) => this.decorateScreenshot(s));
    this.screenshotsLoaded = this.screenshots.length;
    this.matchScreenshotsToLogs();
    this.refreshAvailableAppsFromLoadedScreenshots();

    const currentApp = this.appFilterCtrl.value;
    if (currentApp && currentApp !== "all") {
      const isAppAvailable = this.availableApps.some(app => app.toLowerCase() === currentApp.toLowerCase());
      if (!isAppAvailable) {
        this.appFilterCtrl.setValue("all", { emitEvent: false });
        this.updateQueryParams();
      }
    }

    this.applyReplayFilters();
    this.loadingScreenshots = false;
  }

  private buildBucketsForSelectedDay(
    items: ScreenshotItem[],
    bucketMinutes: BucketSizeMinutes,
    showEmptySlots: boolean
  ): TimelineBucket[] {
    if (!this.selectedDate) return [];
    const bounds = this.getSelectedDayBoundsForApi();
    if (!bounds) return [];

    const startUtc = this.screenshotTime.parseCaptureToUtc(bounds.start, this.shiftTimezone);
    const endUtc = this.screenshotTime.parseCaptureToUtc(bounds.end, this.shiftTimezone);
    if (!startUtc?.isValid || !endUtc?.isValid) return [];

    const startMs = startUtc.toMillis();
    const endMs = endUtc.toMillis();
    const bucketMs = bucketMinutes * 60 * 1000;

    const map = new Map<string, TimelineBucket>();
    for (let bStart = startMs; bStart <= endMs; bStart += bucketMs) {
      const bEnd = Math.min(endMs, bStart + bucketMs);
      const key = String(bStart);
      map.set(key, {
        key,
        startUtcMs: bStart,
        endUtcMs: bEnd,
        label: this.formatBucketLabel(bStart, bEnd),
        screenshots: [],
        usageSeconds: 0,
        idleSeconds: 0,
        state: "no_data",
      });
    }

    const safeItems = Array.isArray(items) ? items : [];
    for (const ss of safeItems) {
      if (!ss?._parsedUtcMs) continue;
      const t = ss._parsedUtcMs;
      if (t < startMs || t > endMs) continue;
      const idx = Math.floor((t - startMs) / bucketMs);
      const bStart = startMs + idx * bucketMs;
      const key = String(bStart);
      const bucket = map.get(key);
      if (bucket) {
        bucket.screenshots.push(ss);
      }
    }

    const logs = Array.isArray(this.flattenedLogSessions) ? this.flattenedLogSessions : [];
    for (const log of logs) {
      const logStart = Math.max(startMs, log.inUtcMs);
      const logEnd = Math.min(endMs, log.outUtcMs);
      if (logEnd <= logStart) continue;
      const logDuration = Math.max(1, logEnd - logStart);
      const idleSec = Math.max(0, Number(log.idleSeconds || 0));
      const usageSec = Math.max(0, Number(log.usageSeconds || 0));

      for (let bStart = Math.floor((logStart - startMs) / bucketMs) * bucketMs + startMs; bStart <= logEnd; bStart += bucketMs) {
        const key = String(bStart);
        const bucket = map.get(key);
        if (!bucket) continue;
        const overlapStart = Math.max(bucket.startUtcMs, logStart);
        const overlapEnd = Math.min(bucket.endUtcMs, logEnd);
        if (overlapEnd <= overlapStart) continue;
        const overlapMs = overlapEnd - overlapStart;
        const ratio = overlapMs / logDuration;
        bucket.usageSeconds += usageSec * ratio;
        bucket.idleSeconds += idleSec * ratio;
      }
    }

    const buckets = Array.from(map.values()).sort((a, b) => a.startUtcMs - b.startUtcMs);
    buckets.forEach((b) => b.screenshots.sort((a, c) => (a._parsedUtcMs || 0) - (c._parsedUtcMs || 0)));
    buckets.forEach((b) => {
      const hasShots = b.screenshots.length > 0;
      const hasUsage = b.usageSeconds > 0;
      const hasIdle = b.idleSeconds > 0;
      if (!hasShots && !hasUsage && !hasIdle) {
        b.state = "no_data";
      } else if (!hasShots && (hasUsage || hasIdle)) {
        b.state = "active_no_screenshot";
      } else if (hasUsage && hasIdle) {
        b.state = "mixed";
      } else if (hasIdle && !hasUsage) {
        b.state = "idle";
      } else {
        b.state = "active";
      }
    });

    if (showEmptySlots) {
      return buckets;
    }
    // Compact view: keep slots that have screenshots or tracked usage/idle.
    return buckets.filter(
      (b) =>
        (b.screenshots?.length || 0) > 0 ||
        b.usageSeconds > 0 ||
        b.idleSeconds > 0
    );
  }

  private formatBucketLabel(startUtcMs: number, endUtcMs: number): string {
    const tz = this.shiftTimezone && this.screenshotTime.isValidIanaZone(this.shiftTimezone) ? this.shiftTimezone : undefined;
    const start = new Date(startUtcMs);
    const end = new Date(endUtcMs);
    const fmt = new Intl.DateTimeFormat(undefined, { hour: "2-digit", minute: "2-digit", timeZone: tz });
    return `${fmt.format(start)} – ${fmt.format(end)}`;
  }

  private groupBucketsByDayPart(
    buckets: TimelineBucket[]
  ): Array<{ label: string; buckets: TimelineBucket[]; bucketCount: number; screenshotCount: number }> {
    const groups = new Map<string, { buckets: TimelineBucket[]; screenshotCount: number }>();
    const tz = this.shiftTimezone && this.screenshotTime.isValidIanaZone(this.shiftTimezone) ? this.shiftTimezone : null;
    for (const b of buckets) {
      const hour = tz
        ? DateTime.fromMillis(b.startUtcMs, { zone: "utc" }).setZone(tz).hour
        : new Date(b.startUtcMs).getHours();
      const label = this.dayPartLabelForHour(hour);
      const entry = groups.get(label) || { buckets: [], screenshotCount: 0 };
      entry.buckets.push(b);
      entry.screenshotCount += (b.screenshots?.length || 0);
      groups.set(label, entry);
    }
    const order: DaypartLabel[] = [...DAYPART_LABELS];
    return order
      .filter((k) => groups.has(k))
      .map((k) => {
        const entry = groups.get(k)!;
        const sorted = entry.buckets.sort((a, c) => a.startUtcMs - c.startUtcMs);
        return { label: k, buckets: sorted, bucketCount: sorted.length, screenshotCount: entry.screenshotCount };
      });
  }

  private seedExpandedBuckets(): void {
    // Scan-first: auto-expand the first 2 buckets that actually have screenshots.
    // Still respects the "max 2 expanded buckets" performance guardrail.
    if (this.expandedBucketKeys.size > 0) {
      // Respect user interactions if they've already expanded/collapsed.
      return;
    }
    const withShots = this.buckets.filter((b) => (b.screenshots?.length || 0) > 0);
    for (const b of withShots.slice(0, 2)) {
      this.expandedBucketKeys.add(b.key);
    }
  }

  private parseDurationToSeconds(input: any): number {
    if (!input || typeof input !== "string") return 0;
    const parts = input.split(":").map((x: string) => Number(x));
    if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return 0;
    return (parts[0] * 3600) + (parts[1] * 60) + parts[2];
  }

  /**
   * Full calendar day for the selected date: 00:00:00–23:59:59 in company shift timezone
   * (or browser-local naive bounds when no shift is configured).
   */
  private getSelectedDayBoundsForApi(): { start: string; end: string } | null {
    if (!this.selectedDate) return null;

    const ymd = this.datePipe.transform(this.selectedDate, "yyyy-MM-dd");
    if (!ymd) return null;

    const startM = moment(ymd, "YYYY-MM-DD").startOf("day");
    const endM = moment(ymd, "YYYY-MM-DD").endOf("day");
    const range = this.screenshotTime.filterBoundsForApi(startM, endM, this.shiftTimezone);
    return { start: range.start, end: range.end };
  }

}
